import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { apiClient, fetchApiBlob } from "../../lib/api";
import type { Photo } from "../../types";
import { PhotoReportPreview } from "./PhotoReportPreview";

export interface PhotoReportStatus {
  id: string;
  status: "pending" | "processing" | "ready" | "error";
  message: string;
  page_count: number;
  pdf_url: string | null;
}
export function PhotoReportControls({
  photos,
  jobId,
  opportunityId,
  disabled,
  onReady,
}: {
  photos: Photo[];
  jobId?: number;
  opportunityId?: number;
  disabled: boolean;
  onReady: (report: PhotoReportStatus | null) => void;
}) {
  const [title, setTitle] = useState("Project photo report");
  const [introduction, setIntroduction] = useState("");
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [logo, setLogo] = useState(true);
  const [captions, setCaptions] = useState(true);
  const [annotations, setAnnotations] = useState(true);
  const [report, setReport] = useState<PhotoReportStatus | null>(null);
  const [url, setUrl] = useState("");
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const version = useRef(0);
  const key = JSON.stringify([
    jobId,
    opportunityId,
    photos.map((p) => [p.id, p.annotations, p.description]),
    title,
    introduction,
    theme,
    logo,
    captions,
    annotations,
  ]);
  useEffect(() => {
    version.current++;
    setReport(null);
    setUrl("");
    setPreview(false);
    setBusy(false);
    setError("");
    onReady(null);
    return () => {
      version.current++;
      onReady(null);
    };
  }, [key, onReady]);
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );
  useEffect(() => {
    if (!report || report.status === "error") return;
    let active = true;
    let timeout: ReturnType<typeof setTimeout>;
    let objectUrl = "";
    const update = async () => {
      try {
        if (report.status === "ready" && report.pdf_url) {
          const blob = await fetchApiBlob(report.pdf_url);
          if (!active) return;
          objectUrl = URL.createObjectURL(blob);
          setUrl(objectUrl);
          setBusy(false);
          setPreview(true);
          onReady(report);
        } else {
          const response = await apiClient.get<PhotoReportStatus>(
            `/api/photos/reports/${report.id}/`,
          );
          if (!active) return;
          if (response.data.status === "error") {
            setError(response.data.message);
            setBusy(false);
          }
          setReport(response.data);
        }
      } catch {
        if (active) {
          setError("Could not load the PDF. Please generate it again.");
          setBusy(false);
        }
      }
    };
    if (report.status === "ready") void update();
    else timeout = setTimeout(update, 1500);
    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [report, onReady]);
  const generate = async () => {
    const current = ++version.current;
    setBusy(true);
    setError("");
    setUrl("");
    setReport(null);
    onReady(null);
    try {
      const response = await apiClient.post<PhotoReportStatus>(
        "/api/photos/reports/",
        {
          photo_ids: photos.map((p) => p.id),
          job: jobId,
          opportunity: opportunityId,
          title: title.trim(),
          introduction: introduction.trim(),
          theme,
          include_logo: logo,
          include_captions: captions,
          include_annotations: annotations,
        },
      );
      if (current !== version.current) return;
      setReport(response.data);
      if (response.data.status === "error") {
        setError(response.data.message);
        setBusy(false);
      }
    } catch (error: unknown) {
      if (current !== version.current) return;
      const data = (error as { responseData?: { error?: string } })
        ?.responseData;
      setError(data?.error || "Could not generate the PDF. Please try again.");
      setBusy(false);
    }
  };
  const download = () => {
    const link = document.createElement("a");
    link.href = url;
    link.download =
      (title
        .trim()
        .replace(/[^a-z0-9 _-]/gi, "")
        .slice(0, 100) || "photo-report") + ".pdf";
    document.body.append(link);
    link.click();
    link.remove();
  };
  return (
    <Box
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "md",
        bgcolor: "background.level1",
      }}
    >
      <Typography level="title-md" sx={{ mb: 1 }}>
        Branded photo PDF
      </Typography>
      <Typography level="body-sm" sx={{ mb: 2 }}>
        Uses your company’s proposal branding. Preview or download without
        sending an email.
      </Typography>
      <Stack
        component="fieldset"
        disabled={disabled || busy}
        spacing={1.5}
        sx={{ p: 0, m: 0, border: 0 }}
      >
        <FormControl required>
          <FormLabel>PDF title</FormLabel>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            slotProps={{ input: { maxLength: 160 } }}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Introduction (optional)</FormLabel>
          <Textarea
            minRows={3}
            maxRows={8}
            value={introduction}
            onChange={(e) => setIntroduction(e.target.value)}
            slotProps={{ textarea: { maxLength: 8000 } }}
            placeholder="Describe the purpose of these photos…"
          />
        </FormControl>
        <FormControl>
          <FormLabel>PDF theme</FormLabel>
          <Select
            value={theme}
            disabled={disabled || busy}
            onChange={(_, value) => value && setTheme(value)}
          >
            <Option value="light">Light</Option>
            <Option value="dark">Dark</Option>
          </Select>
        </FormControl>
        <Stack direction="row" flexWrap="wrap" gap={2}>
          <Checkbox
            label="Company logo"
            checked={logo}
            onChange={(e) => setLogo(e.target.checked)}
          />
          <Checkbox
            label="Photo captions"
            checked={captions}
            onChange={(e) => setCaptions(e.target.checked)}
          />
          <Checkbox
            label="Annotations"
            checked={annotations}
            onChange={(e) => setAnnotations(e.target.checked)}
          />
        </Stack>
      </Stack>
      {error && (
        <Alert color="danger" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {busy && (
        <Typography role="status" sx={{ mt: 2 }}>
          Preparing your PDF… You can email it when the preview is ready.
        </Typography>
      )}
      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
        <Button
          onClick={generate}
          loading={busy}
          disabled={
            disabled ||
            !title.trim() ||
            photos.length === 0 ||
            photos.length > 25
          }
        >
          {url ? "Generate again" : "Generate & preview"}
        </Button>
        {url && (
          <>
            <Button variant="outlined" onClick={() => setPreview(true)}>
              Preview PDF
            </Button>
            <Button variant="soft" onClick={download}>
              Download PDF
            </Button>
            <Typography level="body-sm" sx={{ alignSelf: "center" }}>
              {report?.page_count} page{report?.page_count === 1 ? "" : "s"}
            </Typography>
          </>
        )}
      </Stack>
      <Modal open={preview && !!url} onClose={() => setPreview(false)}>
        <ModalDialog layout="fullscreen" sx={{ p: 2 }}>
          <ModalClose />
          <Typography level="h4">PDF preview</Typography>
          <Typography level="body-sm">
            This is the file that will be downloaded or emailed.
          </Typography>
          {preview && url && <PhotoReportPreview key={url} url={url} />}
          <Stack direction="row" spacing={1}>
            <Button onClick={download}>Download PDF</Button>
            <Button variant="plain" onClick={() => setPreview(false)}>
              Back to sharing
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
